import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { ComicGridCard } from "@/components/base/ComicCard";
import { categories } from "@/mocks/categories";
import { comics } from "@/mocks/comics";
import { searchComics } from "@/utils/comics";

const statusOptions = ["全部", "连载中", "完结"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [cat, setCat] = useState("全部");
  const [status, setStatus] = useState("全部");

  const results = useMemo(() => {
    let list = searchComics(input);
    if (cat !== "全部") list = list.filter((c) => c.categories.includes(cat));
    if (status !== "全部") list = list.filter((c) => c.status === status);
    return list;
  }, [input, cat, status]);

  const hot = useMemo(
    () => [...comics].sort((a, b) => b.views - a.views).slice(0, 6),
    [],
  );

  const onChange = (value: string) => {
    setInput(value);
    if (value.trim()) {
      setParams({ q: value }, { replace: true });
    } else {
      setParams({}, { replace: true });
    }
  };

  const resetFilters = () => {
    setCat("全部");
    setStatus("全部");
  };

  return (
    <>
      <Seo
        title={`${keyword ? `${keyword} 搜索结果` : "全站搜索"} - 韩漫免费观看`}
        description={`在韩漫免费观看在线阅读漫画网站中搜索韩漫，支持按标题、作者模糊搜索，快速找到想看的韩漫作品。`}
        keywords="韩漫搜索,韩漫漫画搜索,韩漫免费观看,在线阅读"
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "搜索结果" }]} />

        <div className="mt-4">
          <h1 className="font-display text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {keyword ? `“${keyword}” 的搜索结果` : "搜索韩漫"}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            支持按漫画标题、作者、题材与标签进行模糊搜索
          </p>
        </div>

        <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5 focus-within:border-primary-300 focus-within:bg-background-50 md:max-w-xl">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              value={input}
              onChange={(e) => onChange(e.target.value)}
              placeholder="输入韩漫标题或作者，如：爱情、白恩熙"
              aria-label="搜索韩漫"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            {input && (
              <button
                type="button"
                onClick={() => onChange("")}
                aria-label="清空"
                className="text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
          <span className="text-sm text-foreground-600">
            共找到 <strong className="font-bold text-primary-700">{results.length}</strong> 部作品
          </span>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-foreground-500">题材</span>
            {["全部", ...categories.map((c) => c.name)].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  cat === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-foreground-500">状态</span>
            {statusOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  status === s
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-secondary-50 hover:text-secondary-900"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {!input.trim() ? (
            <div>
              <h2 className="mb-3 text-base font-semibold text-foreground-900">
                大家都在看
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {hot.map((comic) => (
                  <ComicGridCard key={comic.id} comic={comic} />
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="text-sm text-foreground-500">热门分类：</span>
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : results.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {results.map((comic) => (
                <ComicGridCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              <EmptyState
                icon="ri-search-eye-line"
                title="未找到相关韩漫"
                desc={`没有与“${input}”匹配的作品，换个关键词，或者从热门韩漫和分类入口继续浏览吧。`}
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
              <div>
                <h2 className="mb-3 text-base font-semibold text-foreground-900">
                  为你推荐
                </h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {hot.map((comic) => (
                    <ComicGridCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}